import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography
} from '@material-ui/core';
import Stars from '@material-ui/icons/Stars';


const useStyles = makeStyles(theme => ({
  root: {
    height: '100%'
  },
  chartContainer: {
    position: 'relative',
    height: '300px'
  },
  stats: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center'
  },
  device: {
    textAlign: 'center',
    padding: theme.spacing(1)
  },
  deviceIcon: {
    color: theme.palette.icon
  },
  chart2Header: {
    '& div.MuiCardHeader-action': {
      width: "50%"
    }
  }
}));

const Chart2 = props => {
  const { className, hwLevel, hwSkillGeneral } = props;

  const classes = useStyles();
  const theme = useTheme();

  const dataLevelAvgGrade = [];
  const dataLevelCompletion = [];

  if(Object.keys(hwSkillGeneral).length > 0) {
    for(const k in hwSkillGeneral) {
      dataLevelAvgGrade.push(hwSkillGeneral[k]['average_grad']);
      dataLevelCompletion.push(hwSkillGeneral[k]['percentage_completion']);
    }
  } else if(Object.keys(hwLevel).length > 0) {
    dataLevelAvgGrade.push(hwLevel['Easy']['average_grad']);
    dataLevelAvgGrade.push(hwLevel['Somewhat Easy']['average_grad']);
    dataLevelAvgGrade.push(hwLevel['Medium']['average_grad']);
    dataLevelAvgGrade.push(hwLevel['Somewhat Difficult']['average_grad']);
    dataLevelAvgGrade.push(hwLevel['Difficult']['average_grad']);

    dataLevelCompletion.push(hwLevel['Easy']['percentage_completion']);
    dataLevelCompletion.push(hwLevel['Somewhat Easy']['percentage_completion']);
    dataLevelCompletion.push(hwLevel['Medium']['percentage_completion']);
    dataLevelCompletion.push(hwLevel['Somewhat Difficult']['percentage_completion']);
    dataLevelCompletion.push(hwLevel['Difficult']['percentage_completion']);
  }

  let labels = ['HW Completion - Easy', 'HW Completion - Somewhat Easy', 'HW Completion - Medium', 'HW Completion - Somewhat Difficult', 'HW Completion - Difficult'];
  if(Object.keys(hwSkillGeneral).length > 0) {
    labels = [];
    for(const k in hwSkillGeneral) {
      labels.push('HW Completion - ' + k);
    }
  }

  const data = {
    datasets: [
      {
        data: dataLevelCompletion,
        backgroundColor: [
          theme.palette.text.main,
          theme.palette.primary.main,
          theme.palette.info.main,
          theme.palette.warning.main,
          theme.palette.error.main,
          theme.palette.success.main
        ],
        borderWidth: 8,
        borderColor: theme.palette.white,
        hoverBorderColor: theme.palette.white
      }
    ],
    labels: labels
  };

  const options = {
    legend: {
      display: false
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    cutoutPercentage: 80,
    layout: { padding: 0 },
    tooltips: {
      enabled: true,
      mode: 'index',
      intersect: false,
      borderWidth: 1,
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.white,
      titleFontColor: theme.palette.text.primary,
      bodyFontColor: theme.palette.text.secondary,
      footerFontColor: theme.palette.text.secondary
    }
  };

  let avgGradeLabelTitle = ['Easy', 'Somewhat easy', 'Medium', 'Somewhat difficult', 'Difficult'];
  if(Object.keys(hwSkillGeneral).length > 0) {
    avgGradeLabelTitle = [];
    for(const k in hwSkillGeneral) {
      avgGradeLabelTitle.push(k);
    }
  }

  const devices = [
    {
      title: avgGradeLabelTitle[0],
      value: dataLevelAvgGrade[0],
      icon: <Stars style={{fontSize: "1.2rem"}} />,
      color: theme.palette.text.main
    },
    {
      title: avgGradeLabelTitle[1],
      value: dataLevelAvgGrade[1],
      icon: <Stars style={{fontSize: "1.4rem"}} />,
      color: theme.palette.primary.main
    },
    {
      title: avgGradeLabelTitle[2],
      value: dataLevelAvgGrade[2],
      icon: <Stars style={{fontSize: "1.6rem"}} />,
      color: theme.palette.info.main
    },
    {
      title: avgGradeLabelTitle[3],
      value: dataLevelAvgGrade[3],
      icon: <Stars style={{fontSize: "1.8rem"}} />,
      color: theme.palette.warning.main
    },
    {
      title: avgGradeLabelTitle[4],
      value: dataLevelAvgGrade[4],
      icon: <Stars style={{fontSize: "2rem"}} />,
      color: theme.palette.error.main
    }
  ];

  if(avgGradeLabelTitle[5]) {
    devices.push({
      title: avgGradeLabelTitle[5],
      value: dataLevelAvgGrade[5],
      icon: <Stars style={{fontSize: "2.2rem"}} />,
      color: theme.palette.success.main
    });
  }

  return (
    <Card
      className={clsx(classes.root, className)}
    >
      <CardHeader
        action={
          <Typography variant="overline">Homework Completion and Avg. Grade Percentage</Typography>
        }
        title="Level / Performance"
        className={classes.chart2Header}
      />
      <Divider />
      <CardContent>
        <div className={classes.chartContainer}>
          <Doughnut
            data={data}
            options={options}
          />
        </div>
        <div className={classes.stats}>
          {devices.map(device => (
            <div
              className={classes.device}
              key={device.title}
            >
              <span className={classes.deviceIcon}>{device.icon}</span>
              <Typography variant="body1">{device.title}</Typography>
              <Typography
                style={{ color: device.color }}
                variant="h2"
              >
                {device.value}%
              </Typography>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

Chart2.propTypes = {
  className: PropTypes.string,
  hwLevel: PropTypes.object.isRequired,
  hwSkillGeneral: PropTypes.object.isRequired
};

export default Chart2;
